import React, { useState, useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { getSkills, createSkill, Skill } from '../api/skills';
import { FaTimes } from 'react-icons/fa';

interface SkillsInputProps {
  value: string[] | string | null; // Array of skill names
  onChange: (value: string[]) => void; // Returns array of skill names
  placeholder?: string;
  disabled?: boolean;
}

export const SkillsInput: React.FC<SkillsInputProps> = ({
  value,
  onChange,
  placeholder = 'Type to search skills...',
  disabled = false
}) => {
  const { keycloak } = useKeycloak();
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<Skill[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Parse skills from value prop
  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedSkills(value);
    } else if (typeof value === 'string' && value.trim()) {
      const skills = value
        .split(/[;,]/)
        .map(s => s.trim())
        .filter(s => s.length > 0);
      setSelectedSkills(skills);
    } else {
      setSelectedSkills([]);
    }
  }, [value]);

  // Fetch skills suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!inputValue.trim() || !keycloak?.token) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const skills = await getSkills(keycloak.token, inputValue, 1, 10);
        setSuggestions(skills);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Error fetching skills:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue, keycloak?.token]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addSkill = async (skillName: string) => {
    if (!skillName.trim()) return;
    
    const trimmedName = skillName.trim();
    console.log('🎯 SkillsInput.addSkill called with:', trimmedName);
    
    // Check if skill already in list
    if (selectedSkills.includes(trimmedName)) {
      console.log('⚠️ Skill already in list, skipping');
      setInputValue('');
      setShowSuggestions(false);
      return;
    }
    
    // If token available, ensure skill exists in database
    if (keycloak?.token) {
      try {
        console.log('🔍 Checking if skill exists in database...');
        const existingSkills = await getSkills(keycloak.token, trimmedName, 1, 5);
        const exactMatch = existingSkills.find(s => s.name.toLowerCase() === trimmedName.toLowerCase());
        
        // If skill doesn't exist, create it
        if (!exactMatch) {
          console.log('🆕 Skill not found, creating new skill:', trimmedName);
          await createSkill(keycloak.token, { name: trimmedName });
          console.log('✅ Skill created successfully');
        } else {
          console.log('✅ Skill already exists in database:', exactMatch.id);
        }
      } catch (error) {
        console.error('❌ Error checking/creating skill:', error);
        // Continue anyway - add skill to user even if creation failed
      }
    } else {
      console.log('⚠️ No token available, skipping skill creation in database');
    }
    
    const newSkills = [...selectedSkills, trimmedName];
    console.log('📝 Adding skill to local list. New skills array:', newSkills);
    setSelectedSkills(newSkills);
    onChange(newSkills);
    
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeSkill = (skillToRemove: string) => {
    const newSkills = selectedSkills.filter(s => s !== skillToRemove);
    setSelectedSkills(newSkills);
    onChange(newSkills); // Return array, not string
  };

  const handleInputKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      if (inputValue.trim()) {
        await addSkill(inputValue.trim());
      }
    } else if (e.key === 'Backspace' && !inputValue && selectedSkills.length > 0) {
      // Remove last skill if input is empty and backspace is pressed
      removeSkill(selectedSkills[selectedSkills.length - 1]);
    }
  };

  return (
    <div className="relative">
      {/* Selected Skills */}
      <div className="flex flex-wrap gap-2 mb-2">
        {selectedSkills.map((skill, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 px-3 py-1 bg-[#f5f5f5] text-[#555] rounded-full text-sm"
          >
            {skill}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeSkill(skill)}
                className="hover:bg-[#eaeaea] rounded-full p-0.5 transition-colors"
              >
                <FaTimes className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
      </div>

      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onFocus={() => {
            if (inputValue.trim()) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 disabled:bg-[#f5f5f5] disabled:cursor-not-allowed"
        />

        {/* Loading Indicator */}
        {isLoading && (
          <div className="absolute right-3 top-3">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#eaeaea] border-t-blue-600"></div>
          </div>
        )}

        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && !disabled && (
          <div
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#eaeaea] rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] z-50 max-h-60 overflow-y-auto"
          >
            {suggestions.map((skill) => (
              <button
                key={skill.id}
                type="button"
                onClick={async () => await addSkill(skill.name)}
                className="w-full px-4 py-2 text-left hover:bg-[#fafafa] focus:bg-[#fafafa] focus:outline-none border-b border-[#f5f5f5] last:border-b-0"
              >
                <div className="flex flex-col">
                  <span className="font-medium text-[#171717]">{skill.name}</span>
                  {skill.category && (
                    <span className="text-xs text-[#888]">{skill.category}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Hint */}
      <p className="mt-1 text-xs text-[#888]">
        Type and press Enter, comma, or semicolon to add a skill. Click × to remove.
      </p>
    </div>
  );
};

